import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {
  HxCurrencyPipe,
  HxPaymentApprovalService,
  HxStorageInvoiceService,
  isoDate,
  PaymentApprovalFromReportModel,
  PaymentApprovalRequest,
  StorageInvoiceModel
} from 'hx-services';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorHandlerService } from '@manager-app/service/error-handler.service';
import { HcToastrService } from '@manager-app/service/toastr.service';
import { NgbActiveModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgFor, NgIf } from '@angular/common';
import { HxBankAccountSelectComponent, HxDateSelectComponent, HxInvoiceSelectComponent } from 'hx-component';

interface StorageInvoiceInfoModel extends StorageInvoiceModel {
  debtTotal?: number;
}

@Component({
    selector: 'app-payment-approval-new-from-report',
    templateUrl: './payment-approval-new-from-report.component.html',
    styleUrls: ['./payment-approval-new-from-report.component.css'],
    encapsulation: ViewEncapsulation.None,
    imports: [NgIf, FormsModule, ReactiveFormsModule, HxDateSelectComponent, HxBankAccountSelectComponent, NgFor, HxInvoiceSelectComponent, NgbTooltip, TranslocoPipe, HxCurrencyPipe]
})
export class PaymentApprovalNewFromReportComponent implements OnInit {
  @Input() data!: PaymentApprovalFromReportModel;
  @Output() paymentApprovalSave = new EventEmitter<boolean>();
  isLoading = {
    form: false,
  };
  isEdit = false;
  invoiceFormKeys = {
    bankAccountId: 'bankAccountId',
    note: 'note',
    date: 'date',
    supplierId: 'supplierId',
    storeId: 'storeId',
    paymentDestinationCode: 'paymentDestinationCode',
    invoicePayments: 'invoicePayments',
    total: 'total'
  };
  paymentApprovalFormKeys = {
    invoiceId: 'invoiceId',
    value: 'value',
  };
  paymentApprovalForm!: UntypedFormGroup;
  storageInvoices: StorageInvoiceInfoModel[] = [];

  constructor(
    private aRoute: ActivatedRoute,
    private paymentApprovalService: HxPaymentApprovalService,
    private storageInvoiceService: HxStorageInvoiceService,
    private errorService: ErrorHandlerService,
    private router: Router,
    private toastr: HcToastrService,
    private fb: UntypedFormBuilder,
    private modal: NgbActiveModal,
    private tr: TranslocoService
  ) {
  }

  ngOnInit(): void {
    this.createForm();
    this.loadInvoice();
  }

  dismissModal() {
    this.modal.dismiss();
  }

  asFormArray(path: string): UntypedFormArray {
    return this.paymentApprovalForm.get(path) as UntypedFormArray;
  }

  async onSubmitForm() {
    const obj: PaymentApprovalRequest = this.paymentApprovalForm.getRawValue();
    try {
      await this.paymentApprovalService.savePaymentApproval(obj);
      this.toastr.success(this.tr.translate('payment-approval-new-from-report.ts.paidSuccess'));
      this.paymentApprovalSave.emit(true);
      this.modal.close();
    } catch (err: any) {
      this.errorService.handler(err?.error);
    }
  }

  addItem() {
    const items = this.asFormArray(this.invoiceFormKeys.invoicePayments);
    items.push(this.createPaymentApprovalItem());
  }

  removeItem(index: number) {
    const items = this.asFormArray(this.invoiceFormKeys.invoicePayments);
    items.removeAt(index);
    this.storageInvoices.splice(index, 1);
  }

  onInvoiceChnaged(inv: StorageInvoiceModel | StorageInvoiceModel[] | undefined, index: number) {
    if (inv && !Array.isArray(inv)) {
      this.storageInvoices[index] = inv;
      this.storageInvoices[index].debtTotal = this.getDebtTotal(inv);
    } else {
      this.storageInvoices.splice(index, 1);
    }
  }

  onSupplierChaged() {
    this.clearInvoicePayments();
    this.storageInvoices = [];
  }

  closeModal(modal) {
    this.paymentApprovalSave.emit(false);
    this.clearInvoicePayments();
    this.paymentApprovalForm.reset();
    this.storageInvoices = [];
    if (modal) {
      modal.dismiss();
    }
  }

  onValueChanged() {
    const items = this.asFormArray(this.invoiceFormKeys.invoicePayments);
    let totalValue = 0;
    for (let c of items.controls) {
      const val = (c.value?.value) ? Number(c.value.value) : 0;
      totalValue = totalValue + val;
    }
    this.paymentApprovalForm.controls['total'].patchValue(totalValue);
  }

  private createForm(): void {
    this.paymentApprovalForm = this.fb.group({
      bankAccountId: [''],
      date: [isoDate(Date.now())],
      supplierId: this.data.supplierId,
      storeId: this.data.storeId,
      invoicePayments: this.fb.array([]),
      paymentDestinationCode: [''],
      note: [''],
      total: [''],
    });
  }

  private createPaymentApprovalItem() {
    return this.fb.group({
      invoiceId: [null],
      value: [null],
    });
  }

  private loadInvoice() {
    this.storageInvoiceService.getStorageInvoiceList({
      limit: 1000, storeIds: [this.data.storeId],
      supplierId: this.data.supplierId, createDate: {from: this.data.createDateFrom, to: this.data.createDateTo},
      dueDate: {from: this.data.dueDateFrom, to: this.data.dueDateTo}
    }).subscribe(list => {
      const items = this.asFormArray(this.invoiceFormKeys.invoicePayments);
      let totalValue = 0;
      list.list.forEach(ip => {
        const total = this.getDebtTotal(ip);
        if (total > 0) {
          items.push(this.fb.group({invoiceId: ip.id, value: total}));
          totalValue = totalValue + total;
        }
      });
      console.log('len', items.length);
      this.paymentApprovalForm.controls['total'].patchValue(totalValue);
    });
  }


  private clearInvoicePayments() {
    const items = this.asFormArray(this.invoiceFormKeys.invoicePayments);
    items.clear();
  }

  private getDebtTotal(inv: StorageInvoiceModel): number {
    let total = (inv.totalPrice ?? 0) - (inv.paidTotal ?? 0);
    if (total < 0) {
      total = 0;
    }
    return total;
  }
}
